/**
 * PWA Authentication Service
 * Uses HTTP-only cookies as primary authentication method
 * Falls back to localStorage only for user information and token validation
 */

import { AuthService, AuthToken } from './types';
import { JWTLogic } from './logic';
import { UserLogic } from '@/modules/user';
import type { User } from '@/modules/user';
import { ENV } from '@/modules/config';
import { DEVICE_ID_HEADER, ensureDeviceId } from '@/util/deviceId';

export class PWAAuthService implements AuthService {
  private readonly TOKEN_KEY = 'auth_token'; // For Bearer token fallback

  async getToken(): Promise<string | null> {
    // Bearer mode: return the actual token so apiCall attaches Authorization.
    // Cookie mode: return null — the HttpOnly cookie does the auth, and there's
    // nothing for apiCall to attach. apiCall consults isAuthenticated() to
    // decide whether to send credentials.
    if (ENV.authTransport === 'bearer') {
      return this.getBearerToken();
    }
    return null;
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      // Use fetch directly (not apiCall) to avoid circular dep: api.ts → logic.ts → PWAAuthService → api.ts.
      // Attach X-Device-ID explicitly (apiCall normally adds it) so /me resolves
      // per-device verification state instead of falling back to stale JWT claims (#283).
      const url = `${ENV.apiBaseUrl}/api/users/me`;
      const headers: Record<string, string> = {
        'X-Auth-Transport': ENV.authTransport,
        [DEVICE_ID_HEADER]: await ensureDeviceId(),
      };
      const bearerToken = await this.getBearerToken();
      if (bearerToken) headers['Authorization'] = `Bearer ${bearerToken}`;
      const credentials: RequestCredentials =
        ENV.authTransport === 'bearer' ? 'omit' : 'include';
      const response = await fetch(url, { credentials, headers });
      if (response.ok) {
        const data = await response.json();
        // In bearer mode the backend returns a freshly minted guest token in
        // the body when the request had no JWT. Store it so subsequent apiCall
        // requests include the Authorization header.
        if (ENV.authTransport === 'bearer' && data?.token && !bearerToken) {
          const jwt = JWTLogic.decodeJwt(data.token);
          await this.setToken({
            token: data.token,
            expiresAt: JWTLogic.getExpiration(jwt) ?? undefined,
          });
        }
        return UserLogic.fromNestedUserJSON(data);
      }
      if (response.status === 401 || response.status === 403) return null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to fetch user from API:', error);
    }
    return null;
  }

  async isAuthenticated(): Promise<boolean> {
    // Bearer mode: we're authenticated iff a valid Bearer token is in storage.
    // Cookie mode: we can't read the HttpOnly cookie from JS — authority lives
    // on the server. Return false unless a token is in localStorage (which we
    // only populate as a side effect of auth-establishing responses in
    // unspecified/bearer mode). The source of truth for cookie-mode auth is
    // UserContext's /api/users/me round-trip.
    const bearerToken = await this.getBearerToken();
    return bearerToken !== null;
  }

  async refreshToken(): Promise<AuthToken | null> {
    // For HTTP-only cookie approach, we don't need to refresh tokens
    // The backend handles token validation via HTTP-only cookies
    // Just-in-time validation: check if we have a Bearer token stored
    const bearerToken = await this.getBearerToken();
    if (bearerToken) {
      // Get actual expiration from Bearer token
      const jwt = JWTLogic.decodeJwt(bearerToken);
      const jwtExpiration = JWTLogic.getExpiration(jwt);
      // Guest tokens may have no expiration (indefinite), which is valid
      return {
        token: 'http-only-cookie',
        expiresAt: jwtExpiration,
      };
    }
    return null;
  }

  async setToken(token: AuthToken): Promise<void> {
    // Store Bearer token in localStorage for fallback scenarios
    localStorage.setItem(this.TOKEN_KEY, JSON.stringify(token));
  }

  async clearToken(): Promise<void> {
    // Clear Bearer token from localStorage
    localStorage.removeItem(this.TOKEN_KEY);
  }

  /**
   * Handle authentication failure by falling back to Bearer tokens
   * This should be called when an API request fails due to authentication issues
   */
  async handleAuthFailure(): Promise<void> {
    // eslint-disable-next-line no-console
    console.warn('HTTP-only cookies failed, falling back to Bearer tokens');
    // Bearer token is already stored and will be used automatically on retry
  }

  /**
   * Get Bearer token for fallback scenarios
   */
  async getBearerToken(): Promise<string | null> {
    const tokenData = localStorage.getItem(this.TOKEN_KEY);
    if (!tokenData) {
      return null;
    }

    try {
      const token: AuthToken = JSON.parse(tokenData);

      // Check if token is expired using actual JWT expiration (not calculated time)
      const jwt = JWTLogic.decodeJwt(token.token);
      if (JWTLogic.isExpired(jwt)) {
        await this.clearToken();
        return null;
      }

      return token.token;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to parse stored token:', error);
      await this.clearToken();
      return null;
    }
  }

  /**
   * Detect if an error is CORS-related
   */
  private isCorsError(error: unknown): boolean {
    if (!error) return false;

    // Check for common CORS error patterns
    if (error instanceof Error && error.name === 'TypeError') {
      const message = error.message?.toLowerCase() || '';
      return (
        message.includes('cors') ||
        message.includes('cross-origin') ||
        message.includes('network error') ||
        message.includes('failed to fetch')
      );
    }

    // Check for status 0 (often indicates CORS issues)
    if (typeof error === 'object' && error !== null && 'status' in error) {
      return (error as { status: number }).status === 0;
    }

    return false;
  }
}
